import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import CompanyLogo from "../../../assets/images/Sidebar/logo/amelio-logo.png";
import CompanyRoundedLogo from "../../../assets/images/amelio-rounded-logo.png";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CryptoJS from "crypto-js";
import {
  AmelioManagementAccountIcon,
  DashBoardIcon,
  DepartmentManagementIcon,
  ExportHistoryIcon,
  HolidaysManagementIcon,
  HsFormMappingIcon,
  InvoiceManagementIcon,
  LeakDetectionIcon,
  LeavesManagementIcon,
  ProjectManagementIcon,
  RolesAndPermissionIcon,
  SelectModuleIcon,
  SupplierManagementIcon,
  TimelogManagementIcon,
  UserManagementIcon,
  VehicleManagementIcon,
} from "./SideBarSVGs";

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

type MenuItem = {
  label: string;
  icon: string;
  to: string;
};

const decryptMenuItems = (encryptedData: string, secretKey: string) => {
  const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
  const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
  return JSON.parse(decryptedData);
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar }) => {
  const [items, setItems] = useState<MenuItem[]>([]);
  const location = window.location;

  const navigate = useNavigate();

  useEffect(() => {
    let menuItems: any = [];

    if (
      localStorage.userEmail === "jakub@ameliogroup.co.uk" &&
      localStorage.currentModuleID === "2"
    ) {
      menuItems = [
        {
          label: "Amelio Management Account",
          to: "generate-report",
          icon: "amelioManagement",
        },
      ];
    } else {
      const encryptedMenuItems = localStorage.getItem("encryptedMenuItems");
      if (encryptedMenuItems) {
        const decryptedMenuItems = decryptMenuItems(
          encryptedMenuItems,
          process.env.REACT_APP_MENU_SECRET_KEY ||
            "F0vfA9xAhMFrSYv8Glm8uIA3mKzPx6Wr",
        );
        menuItems = decryptedMenuItems.map((menu: any) => ({
          label: menu.displayName,
          icon: menu.icon, // Store the icon key for later mapping
          to: `/layout/${menu.url}`, // Convert the url to match the desired format
        }));
      }
    }
    setItems(menuItems);
  }, []);

  const getMenuIcon = (iconKey: string) => {
    return iconMapping[iconKey] || <span className="default-icon"></span>; // Fallback to a default icon or span
  };

  const iconMapping: { [key: string]: React.ReactNode } = {
    invoiceManagement: <InvoiceManagementIcon />,
    supplierManagement: <SupplierManagementIcon />,
    userManagement: <UserManagementIcon />,
    amelioManagement: <AmelioManagementAccountIcon />,
    rolesAndPermission: <RolesAndPermissionIcon />,
    exportHistory: <ExportHistoryIcon fill={"#ffffff"} />,
    dashBoard: <DashBoardIcon />,
    timeLogManagement: <TimelogManagementIcon />,
    vehicleManagement: <VehicleManagementIcon />,
    leaves: <LeavesManagementIcon />,
    holidays: <HolidaysManagementIcon />,
    projectManagement: <ProjectManagementIcon />,
    department: <DepartmentManagementIcon />,
    leakDetection: <LeakDetectionIcon />,
    leakDetectionDashBoard: <DashBoardIcon />,
    hsFormMapping: <HsFormMappingIcon />,
    jobSheetBelfast: <LeakDetectionIcon />,
    jobSheetGloucester: <LeakDetectionIcon />,
    observeInformReport: <LeakDetectionIcon />,
  };

  const Logout = () => {
    localStorage.clear();
    navigate("/");
  };

  const capitalizeWords = (str: string) => {
    return (
      str
        // .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  return (
    <div
      className={`tw-h-screen tw-bg-[#152846] tw-text-white tw-flex tw-flex-col tw-fixed tw-top-0 tw-left-0 tw-transition-all tw-duration-300 ${
        isOpen ? "tw-w-72" : "tw-w-20"
      }`}
      id="sidebar"
    >
      {/* Sidebar header with toggle button */}
      <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
        {/* Display company logo if sidebar is open */}
        {isOpen && (
          <img
            src={CompanyLogo}
            alt="Logo"
            className="tw-h-10 tw-cursor-pointer"
            onClick={() => navigate(localStorage.currentModuleUrl ?? "/")}
          />
        )}
        <div>
          {/* Toggle icon */}
          {!isOpen && (
            <>
              <img
                id="amelioLogo"
                src={CompanyRoundedLogo}
                alt="Logo"
                className="tw-h-10 tw-ml-[6px] tw-cursor-pointer"
                onClick={() => navigate(localStorage.currentModuleUrl ?? "/")}
              />
              <Tooltip target="#amelioLogo">Amelio Group</Tooltip>
            </>
          )}
        </div>
        {
          <div className="tw-absolute tw-right-[-20px] tw-top-[25px]">
            <Button
              id="sidebarToggleBtn"
              className="tw-rounded-full tw-p tw-mt-10 tw-mr-[8px] hover:tw-bg-[#33568f] tw-bg-[#00AFEE] focus:tw-shadow-none"
              onClick={() => toggleSidebar()}
            >
              {!isOpen ? <ChevronRight /> : <ChevronLeft />}
            </Button>
          </div>
        }
      </div>

      <ul className="tw-p-4 tw-overflow-y-scroll tw-h-screen custom-scrollbar ">
        {localStorage.moduleID &&
          localStorage?.moduleID?.split(",").length > 1 && (
            <li className="tw-mb-3 t tw-text-5xl tw-h-[40px] tw-w-full">
              <NavLink
                to="/layout/select-module"
                id="selectModule"
                className={`menu-item tw-flex ${
                  isOpen ? "" : "tw-flex-col"
                } tw-justify-start tw-items-center tw-gap-2 tw-text-white tw-no-underline tw-rounded-md tw-px-3 tw-py-2 tw-w-full tw-text-left hover:tw-bg-[#00AFEE] tw-transition-colors tw-duration-200`}
              >
                {/* Only show the label if sidebar is open */}
                <span className="tw-ml-[-5px]">
                  <SelectModuleIcon />
                </span>
                {isOpen && (
                  <span className="tw-text-white tw-text-sm tw-font-['Inter'] tw-ml-1 tw-leading-normal">
                    Select Module
                  </span>
                )}
                {!isOpen && (
                  <Tooltip target="#selectModule">Select Module</Tooltip>
                )}
              </NavLink>
            </li>
          )}
        {items.map((item, index) => {
          const isActive =
            location.pathname === item.to ||
            (item.to === "/layout/invoice-management" &&
              location.pathname === "/layout/add-invoice") ||
            (item.to === "/layout/leak-detection/team-listing" &&
              (location.pathname ===
                "/layout/leak-detection/add-risk-assessment" ||
                location.pathname ===
                  "/layout/leak-detection/view-detection")) ||
            (item.to === "/layout/leak-detection/vehicle-assessment-list" &&
              (location.pathname ===
                "/layout/leak-detection/add-vehicle-assessment" ||
                location.pathname ===
                  "/layout/leak-detection/view-vehicle-assessment")) ||
            (item.to === "/layout/health-and-safety/jobsheet-belfast" &&
              (location.pathname ===
                "/layout/health-and-safety/add-jobsheet-belfast" ||
                location.pathname ===
                  "/layout/health-and-safety/view-jobsheet-belfast" ||
                location.pathname ===
                  "/layout/health-and-safety/edit-jobsheet-belfast")) ||
            (item.to === "/layout/health-and-safety/jobsheet-gloucester" &&
              (location.pathname ===
                "/layout/health-and-safety/add-jobsheet-gloucester" ||
                location.pathname ===
                  "/layout/health-and-safety/view-jobsheet-gloucester" ||
                location.pathname ===
                  "/layout/health-and-safety/edit-jobsheet-gloucester")) ||
            (item.to ===
              "/layout/health-and-safety/pre-use-equipment-checklist" &&
              (location.pathname ===
                "/layout/health-and-safety/add-equipment-checklist" ||
                location.pathname ===
                  "/layout/health-and-safety/view-equipment-checklist")) ||
            (item.to ===
              "/layout/health-and-safety/pre-use-confined-space-equipment" &&
              (location.pathname ===
                "/layout/health-and-safety/add-confined-spaces-equipment" ||
                location.pathname ===
                  "/layout/health-and-safety/view-confined-spaces-equipment")) ||
            (item.to ===
              "/layout/health-and-safety/permit-for-confined-space-entry" &&
              (location.pathname ===
                "/layout/health-and-safety/add-permit-space-entry" ||
                location.pathname ===
                  "/layout/health-and-safety/view-permit-space-entry")) ||
            (item.to === "/layout/health-and-safety/environment-surveying" &&
              (location.pathname ===
                "/layout/health-and-safety/add-environment-surveying" ||
                location.pathname ===
                  "/layout/health-and-safety/view-environmental-surveying")) ||
            (item.to === "/layout/health-and-safety/observe-inform-report" &&
              (location.pathname ===
                "/layout/health-and-safety/add-observe-inform-report" ||
                location.pathname ===
                  "/layout/health-and-safety/view-observe-inform-report"));
          const tooltipId = `tooltip_${index}`;

          return (
            <li key={index} className="tw-mb-3 tw-list-none">
              <NavLink
                to={item.to}
                className={`menu-item tw-flex ${
                  isOpen ? "" : "tw-flex-col"
                } tw-justify-start tw-items-center tw-gap-2 tw-text-white tw-no-underline tw-rounded-md tw-px-3 tw-py-2 tw-w-full tw-text-left hover:tw-bg-[#00AFEE] tw-transition-colors tw-duration-200 ${
                  isActive ? "tw-bg-[#00AFEE]" : ""
                }`}
                id={tooltipId}
                {...(!isOpen && {
                  "data-pr-tooltip": item.label,
                  "data-pr-position": "right",
                  "data-pr-at": "right center",
                  "data-pr-my": "left center-2",
                })}
              >
                {getMenuIcon(item.icon)}
                {/* Only show the label if sidebar is open */}
                {isOpen && (
                  <span className="tw-text-white tw-text-sm tw-font-['Inter'] tw-leading-normal">
                    {item.label}
                  </span>
                )}
                {!isOpen && (
                  <Tooltip target={`#${tooltipId}`}>{item.label}</Tooltip>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>

      {/* Sidebar footer */}
      <div className="tw-mt-auto tw-p-4 tw-mx-4 tw-border-t tw-border-white">
        <div className="tw-flex tw-items-center tw-justify-between">
          {/* Display user info only if sidebar is open */}
          {isOpen && (
            <div className="tw-w-full tw-flex tw-justify-between tw-items-center">
              <div>
                <div className="tw-flex tw-mb-2">
                  <p className="tw-text-xs tw-mr-2">
                    {capitalizeWords(localStorage.displayName || "")}
                  </p>

                  <p className="tw-ml-[3px tw-text-xs tw-mt-[0.4px] tw-right-0">
                    (Amelio Group)
                  </p>
                </div>
                <div className="tw-flex tw-justify-between tw-items-center tw-gap-4 tw-w-full">
                  <p className="tw-text-[#00AFEE] tw-text-xs tw-flex tw-justify-start tw-items-center tw-gap-2 tw-flex-wrap">
                    {localStorage.roleName &&
                      localStorage.roleName
                        .split(",")
                        .map((word: string, index: any) => (
                          <span key={index}>
                            {word.charAt(0).toUpperCase() + word.slice(1)}
                          </span>
                        ))}
                  </p>
                </div>
              </div>
              <button
                type="button"
                id="logoutBtn"
                className={`tw-p-button-rounded tw-p-button-text tw-text-white ${
                  localStorage.roleID === "1" ? "" : ""
                } tw-mt-2`}
                onClick={Logout}
              >
                <i className="pi pi-sign-out"></i>
              </button>
              {!isOpen && <Tooltip target="#logoutBtn">Logout</Tooltip>}
            </div>
          )}
          {!isOpen && (
            <button
              type="button"
              id="logoutBtn"
              className="tw-p-button-rounded tw-p-button-text tw-text-white"
              onClick={Logout}
            >
              <i className="pi pi-sign-out"></i>
            </button>
          )}
          {!isOpen && <Tooltip target="#logoutBtn">Logout</Tooltip>}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
